// Considered: SpherePerspectiveRow — rejected. It is bound to a runtime `Sphere` (renders an
// avatar + assigned-sphere avatar for one live assignment); this row renders a schema-time
// perspective DEFINITION (cardinality, global, permission set, relation, and eligible-assignee
// descriptors resolved from structural_guidance) — there is no sphere instance to point at.
// See spec Design System Reuse Audit.
//
// CLOSED summary row, the same shape the rule row took in Phase 1c. The contract
// (`type-perspectives.jsx:230-329`) renders a perspective as one line — glyph, name, the
// cardinality word, `· required`, and the provenance chips — that OPENS into the contract's
// `PerspEditor` (`PerspectiveEditorMolecule`), which is the body below.
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'localization';
import { getTranslation } from '@center-platform/spheres/identity';
import type { PerspectiveDropReason, SchemaPerspective, SchemaStructuralGuidance } from 'types/schema';
import TagAtom from '../../atoms/Tag/TagAtom';
import { type TypeFieldRowMode } from './TypeFieldRowMolecule';
import PerspectiveEditorMolecule, {
  PERSPECTIVE_EDITOR_PARITY,
  type PerspectiveAssigneeKind,
  type PerspectiveEditorField,
  type PerspectiveEditorParity,
} from './PerspectiveEditorMolecule';
import { assigneeSentence } from './assigneeSentence';
import './TypePerspectiveRowMolecule.scss';

/**
 * The contract datum ids for ONE mounting of the row. The prototype mounts the same `Row`
 * twice — the live list, and the *Not on this type* group below it — and the decomposition
 * gives each mounting its own ids, so the ids are data rather than literals in the markup
 * (the same seam `PerspectiveEditorParity` gives the two editor call sites).
 */
export interface TypePerspectiveRowParity {
  tile: string;
  name: string;
  meta: string;
  required: string;
  global: string;
  platform: string;
  from: string;
  overriddenHere: string;
  own: string;
  dropped: string;
  reasons: string;
  /** The six row actions (`type-perspectives.jsx:286-312`), in contract order. */
  refuse: string;
  accept: string;
  suppress: string;
  delete: string;
  unsuppress: string;
  undoDelete: string;
}

/** The row as the LIVE list mounts it (`persp > list > each(live) > <Row>`). */
export const PERSPECTIVE_ROW_PARITY: TypePerspectiveRowParity = {
  tile: 'persp.tile',
  name: 'persp.r-name',
  meta: 'persp.many',
  required: 'persp.required',
  global: 'persp.global',
  platform: 'persp.platform',
  from: 'persp.from',
  overriddenHere: 'persp.overridden-here',
  own: 'persp.just-declared',
  dropped: 'persp.suppressed',
  reasons: 'persp.r-reasons-join',
  refuse: 'persp.refuse-it-leave-it-off-this-type-s-accep',
  accept: 'persp.accept-it-again',
  suppress: 'persp.suppress-applies-last-and-beats-everythi',
  delete: 'persp.button',
  unsuppress: 'persp.stop-suppressing-it',
  undoDelete: 'persp.undo-the-delete',
};

/** The same row as the *Not on this type* group mounts it (`persp > p-gone > list > each(gone)`). */
export const PERSPECTIVE_GONE_ROW_PARITY: TypePerspectiveRowParity = {
  tile: 'persp.tile#2',
  name: 'persp.r-name#2',
  meta: 'persp.many#2',
  required: 'persp.required#2',
  global: 'persp.global#2',
  platform: 'persp.platform#2',
  from: 'persp.from#2',
  overriddenHere: 'persp.overridden-here#2',
  own: 'persp.just-declared#2',
  dropped: 'persp.suppressed#2',
  reasons: 'persp.r-reasons-join#2',
  refuse: 'persp.refuse-it-leave-it-off-this-type-s-accep#2',
  accept: 'persp.accept-it-again#2',
  suppress: 'persp.suppress-applies-last-and-beats-everythi#2',
  delete: 'persp.button#2',
  unsuppress: 'persp.stop-suppressing-it#2',
  undoDelete: 'persp.undo-the-delete#2',
};

/**
 * Why a row is not on this type. The merge answers with three
 * (`PerspectiveDropReason`); `deleted` is the fourth and is COMPOSER-SESSION only — a
 * declaration this session removed and has not saved, which the contract keeps in the list
 * with an undo rather than making it vanish (`type-perspectives.jsx:195-199`).
 */
export type PerspectiveRowDropReason = PerspectiveDropReason | 'deleted';

export interface TypePerspectiveRowMoleculeProps {
  perspective: SchemaPerspective;
  /** This perspective's entry from `Schema.structural_guidance`, matched by `system_name`. */
  guidance?: SchemaStructuralGuidance['perspectives'][number];
  /**
   * The inherited-from ancestor's resolved display name (B7). `perspective.inherited_from_schema`
   * is a `schema_id` on the wire — this component never renders it directly. The caller resolves
   * it through its lineage walk; omitted (unresolvable/unviewable ancestor) falls back to the
   * provenance chip's own unknown-ancestor label rather than ever falling back to the raw id.
   */
  inheritedFromName?: string;
  className?: string;
  /** @see TypeFieldRowMode. Defaults to `'disabled'`, same as the row it wraps. */
  mode?: TypeFieldRowMode;
  /**
   * True when this type redeclares a perspective an ancestor also declares — the row overlays
   * the ancestor's definition field by field rather than adding a second standing
   * (`type-perspectives.jsx:263-266`). Mutually exclusive with the plain inherited chip.
   */
  overriddenHere?: boolean;
  /** True for a perspective declared in this editing session and not yet published. */
  justAdded?: boolean;
  /** Open state of the row's editor body. The caller owns it — only one row opens at a time. */
  isOpen?: boolean;
  /** Toggles `isOpen`. Omitted for a platform-owned row, which never opens. */
  onToggle?: () => void;
  /** Asks to delete this type's own declaration. Only ever supplied for a non-inherited row in `edit` mode. */
  onDelete?: () => void;
  /** The type's display name, for the delete affordance's description. */
  typeName?: string;
  /**
   * What each field would be with NO overlay — the ancestor's definition for an inherited
   * perspective. Defaults to `perspective`, which is correct for a row this type declares
   * itself: there is nothing above it to follow.
   */
  base?: SchemaPerspective;
  /** This session's field overlay, keyed as `SchemaPerspective` fields. */
  edits?: Partial<SchemaPerspective>;
  /** The permission words the editor may offer — the caller's, never a second list here. */
  permissionVocabulary?: string[];
  /** The kinds of thing that may hold this standing, from the caller's own type catalog. */
  assigneeKinds?: PerspectiveAssigneeKind[];
  /** A field changed in the open editor. */
  onEdit?: (field: PerspectiveEditorField, value: unknown) => void;
  /** `null` reverts every field — one lever for the whole row. */
  onRevert?: (field: PerspectiveEditorField | null) => void;
  /**
   * Suppress this inherited perspective — the only lever a global answers to, and the one
   * that beats every other. Opens the ask; it never removes anything on its own.
   */
  onSuppress?: () => void;
  /** Stop suppressing it — the undo for the act above (`type-perspectives.jsx:305-307`). */
  onUnsuppress?: () => void;
  /**
   * Refuse this inherited perspective — leave it off this type's accepted list
   * (`type-perspectives.jsx:289-291`). A global answers to no accepted list, so it is never
   * offered one; suppression is the lever there.
   */
  onRefuse?: () => void;
  /** Put a refused perspective back on the accepted list (`type-perspectives.jsx:293-295`). */
  onAccept?: () => void;
  /** Undo a delete this session made, before it is saved (`type-perspectives.jsx:309-313`). */
  onUndoDelete?: () => void;
  /**
   * The rail's portal target for this row's open editor (`persp > list > each(live) > <Row> >
   * list-item > <PerspEditor>` rebinds to `side` — Waivers section, 2026-09-15). When set, the
   * editor mounts THERE instead of inline below the row; the relation/assignee readback (both
   * server-derived, neither in the contract) stays in the row's own body either way. `undefined`
   * (the *Not on this type* group, which never opens, and every existing story/spec) keeps the
   * editor inline, same as before this rebinding.
   */
  editorPortalNode?: HTMLDivElement | null;
  /** Which mounting's contract datum ids this row emits. Defaults to the live list's. */
  parity?: TypePerspectiveRowParity;
  /** Which mounting's contract datum ids the open body's field editor emits. Defaults to the live list's. */
  editorParity?: PerspectiveEditorParity;
  /**
   * Why this perspective does not reach the type (EAINT-897). Set only on rows in the
   * *Not on this type* group; a live row never carries one, and the row does not open.
   */
  dropped?: PerspectiveRowDropReason;
  /** The human sentences behind the drop, joined into one note under the row. */
  reasons?: string[];
  /**
   * The list this row is one item of, as the parity walker's `data-container` path —
   * `perspectives > list > each(live)` for the live list, `… > list#2 > each(gone)` for
   * *Not on this type*. The row is the contract's `list-item`, and its closed line is the
   * inner `list-item` (`type-perspectives.jsx:241-242`, `.p-row-wrap > .p-row`).
   */
  container?: string;
}

const TypePerspectiveRowMolecule: React.FC<TypePerspectiveRowMoleculeProps> = ({
  perspective,
  guidance,
  inheritedFromName,
  className = '',
  mode = 'disabled',
  overriddenHere = false,
  justAdded = false,
  isOpen = false,
  onToggle,
  onDelete,
  typeName,
  base,
  edits,
  permissionVocabulary = [],
  assigneeKinds = [],
  onEdit,
  onRevert,
  onSuppress,
  onUnsuppress,
  onRefuse,
  onAccept,
  onUndoDelete,
  editorPortalNode,
  parity = PERSPECTIVE_ROW_PARITY,
  editorParity = PERSPECTIVE_EDITOR_PARITY,
  dropped,
  reasons,
  container,
}) => {
  const { t, i18n } = useTranslation();
  const rootClasses = ['type-perspective-row', className].filter(Boolean).join(' ');
  const editable = mode === 'edit';
  // The platform's own standing is the permission floor: reword it and every permission
  // check downstream means something different, so it does not open
  // (`type-perspectives.jsx:235-236`).
  const locked = perspective.is_system === true;
  const displayName = getTranslation(i18n, perspective.label_key, perspective.label || perspective.name);
  // A dropped row is not on this type, so there is nothing on it to edit — the contract
  // makes it un-clickable (`type-perspectives.jsx:243`), not a row that opens onto an editor.
  const canOpen = !!onToggle && !locked && !dropped;
  const assigneeReading = assigneeSentence(perspective.assignee_filter ?? [], assigneeKinds, t);

  return (
    <div
      className={rootClasses}
      data-container={container ? `${container} > list-item` : 'list-item'}
      data-testid={`type-perspective-row-${perspective.system_name}`}
      data-open={isOpen ? '1' : undefined}
      data-inherited={perspective.is_inherited ? '1' : undefined}
    >
      {/*
        The prototype makes the whole row clickable and stops propagation on the act
        buttons inside it (`type-perspectives.jsx:241-244`). Nesting real buttons inside a
        `role="button"` row is not reachable by keyboard in that order, so the affordances
        are explicit here instead: the word that says what opening does IS the control, and
        the row's acts sit beside it on the same line.
      */}
      <div className="type-perspective-row__line" data-container="list-item">
        <span className="type-perspective-row__glyph" data-datum={parity.tile} data-kind="tile">
          <i className="pi pi-eye" aria-hidden="true" />
        </span>
        <span className="type-perspective-row__name" data-datum={parity.name} data-kind="label">
          {displayName}
        </span>
        {/*
          The cardinality word, said the way the contract says it — "many" / "one", not the
          enum's title-cased label. `· required` is emphasised inside the same meta line
          because it is the one part of it that changes what an author must do.
        */}
        <span className="type-perspective-row__meta" data-datum={parity.meta} data-kind="hint">
          {t(`studio.typeEditor.perspectives.cardinalityWord.${perspective.cardinality}`)}
          {perspective.required && (
            <strong className="type-perspective-row__required" data-datum={parity.required} data-kind="strong">
              {t('studio.typeEditor.perspectives.requiredMark')}
            </strong>
          )}
          {/*
            Who can hold it — "a person, a role or an identity record" — is what the row is
            FOR, so the closed line says it (`type-perspectives.jsx:253`, `assigneeSay`),
            in the same words the open editor's *Resolves to* uses.
          */}
          {assigneeReading && (
            <span className="type-perspective-row__assignees">
              {t('studio.typeEditor.perspectives.assigneeMark', { assignees: assigneeReading })}
            </span>
          )}
        </span>
        <span className="type-perspective-row__tags" data-container="chips">
          {perspective.global && (
            <TagAtom
              tone="info"
              size="small"
              label={t('studio.typeEditor.perspectives.chip.global')}
              title={t('studio.typeEditor.perspectives.chip.globalReason')}
              data-testid="type-perspective-row-global"
              data-datum={parity.global}
              data-kind="chip"
            />
          )}
          {locked && (
            <TagAtom
              tone="neutral"
              size="small"
              label={t('studio.typeEditor.perspectives.chip.platform')}
              data-testid="type-perspective-row-platform"
              data-datum={parity.platform}
              data-kind="chip"
            />
          )}
          {perspective.is_inherited && !overriddenHere && (
            <TagAtom
              tone="outline"
              size="small"
              className="type-perspective-row__from-tag"
              label={t('studio.typeEditor.perspectives.chip.from', {
                name: inheritedFromName ?? t('studio.typeEditor.row.unknownAncestor'),
              })}
              data-testid="type-perspective-row-inherited-from"
              data-datum={parity.from}
              data-kind="chip"
            />
          )}
          {overriddenHere && (
            <TagAtom
              tone="warn"
              size="small"
              label={t('studio.typeEditor.perspectives.chip.overriddenHere')}
              title={t('studio.typeEditor.perspectives.chip.overriddenHereReason', {
                name: inheritedFromName ?? t('studio.typeEditor.row.unknownAncestor'),
              })}
              data-testid="type-perspective-row-overridden"
              data-datum={parity.overriddenHere}
              data-kind="chip"
            />
          )}
          {!perspective.is_inherited && !perspective.global && (
            <TagAtom
              tone="success"
              size="small"
              className="type-perspective-row__own-tag"
              label={t(
                justAdded
                  ? 'studio.typeEditor.perspectives.chip.justDeclared'
                  : 'studio.typeEditor.perspectives.chip.declaredHere'
              )}
              data-testid="type-perspective-row-own"
              data-datum={parity.own}
              data-kind="chip"
            />
          )}
          {dropped && (
            <TagAtom
              tone="danger"
              size="small"
              label={t(`studio.typeEditor.perspectives.chip.dropped.${dropped}`)}
              data-testid="type-perspective-row-dropped"
              data-datum={parity.dropped}
              data-kind="chip"
            />
          )}
        </span>
        {/*
          The prototype hangs the platform row's reason off a `title` tooltip. This is
          precisely a state whose reason must not be hidden — it is why the row does not
          open — so it renders as text (ux-craft I2), the same call the rule row's dormant
          marker made.
        */}
        {locked && (
          <span className="type-perspective-row__locked-reason">
            {t('studio.typeEditor.perspectives.chip.platformReason')}
          </span>
        )}
        {canOpen && (
          <button
            type="button"
            className="type-perspective-row__open"
            aria-expanded={isOpen}
            aria-label={t('studio.typeEditor.perspectives.openRowAria', { name: displayName })}
            onClick={onToggle}
          >
            {t(
              perspective.is_inherited
                ? 'studio.typeEditor.perspectives.row.override'
                : 'studio.typeEditor.perspectives.row.configure'
            )}
            {/* The disclosure cue (`type-perspectives.jsx:305-307`): a row that opens says so. */}
            <i
              className={`type-perspective-row__chevron pi ${isOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`}
              aria-hidden="true"
            />
          </button>
        )}
        {onRefuse && (
          <button
            type="button"
            className="type-perspective-row__act"
            title={t('studio.typeEditor.perspectives.row.refuseTitle')}
            aria-label={t('studio.typeEditor.perspectives.row.refuseTitle')}
            data-testid={`type-perspective-row-refuse-${perspective.system_name}`}
            data-datum={parity.refuse}
            data-kind="button"
            onClick={onRefuse}
          >
            <i className="pi pi-list" aria-hidden="true" />
            <i className="pi pi-times type-perspective-row__act-mark" aria-hidden="true" />
          </button>
        )}
        {onAccept && (
          <button
            type="button"
            className="type-perspective-row__act"
            title={t('studio.typeEditor.perspectives.row.acceptTitle')}
            aria-label={t('studio.typeEditor.perspectives.row.acceptTitle')}
            data-testid={`type-perspective-row-accept-${perspective.system_name}`}
            data-datum={parity.accept}
            data-kind="button"
            onClick={onAccept}
          >
            <i className="pi pi-list" aria-hidden="true" />
            <i className="pi pi-plus type-perspective-row__act-mark" aria-hidden="true" />
          </button>
        )}
        {onSuppress && (
          <button
            type="button"
            className="type-perspective-row__act"
            title={t('studio.typeEditor.perspectives.row.suppressTitle')}
            aria-label={t('studio.typeEditor.perspectives.row.suppressTitle')}
            data-testid={`type-perspective-row-suppress-${perspective.system_name}`}
            data-datum={parity.suppress}
            data-kind="button"
            onClick={onSuppress}
          >
            <i className="pi pi-minus-circle" aria-hidden="true" />
          </button>
        )}
        {onUnsuppress && (
          <button
            type="button"
            className="type-perspective-row__act"
            title={t('studio.typeEditor.perspectives.row.unsuppressTitle')}
            aria-label={t('studio.typeEditor.perspectives.row.unsuppressTitle')}
            data-testid={`type-perspective-row-unsuppress-${perspective.system_name}`}
            data-datum={parity.unsuppress}
            data-kind="button"
            onClick={onUnsuppress}
          >
            <i className="pi pi-replay" aria-hidden="true" />
          </button>
        )}
        {onUndoDelete && (
          <button
            type="button"
            className="type-perspective-row__act"
            title={t('studio.typeEditor.perspectives.row.undoDeleteTitle')}
            aria-label={t('studio.typeEditor.perspectives.row.undoDeleteTitle')}
            data-testid={`type-perspective-row-undo-delete-${perspective.system_name}`}
            data-datum={parity.undoDelete}
            data-kind="button"
            onClick={onUndoDelete}
          >
            <i className="pi pi-replay" aria-hidden="true" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className="type-perspective-row__act"
            title={t('studio.typeEditor.perspectives.row.deleteTitle', { type: typeName ?? '' })}
            aria-label={t('studio.typeEditor.perspectives.row.deleteTitle', { type: typeName ?? '' })}
            data-testid={`type-perspective-row-delete-${perspective.system_name}`}
            data-datum={parity.delete}
            data-kind="button"
            onClick={onDelete}
          >
            <i className="pi pi-trash" aria-hidden="true" />
          </button>
        )}
      </div>

      {/*
        `type-perspectives.jsx:320-322` — the reason the row is not here, said under the row it
        is about. The chip states the FACT; a person reading the ancestor needs the reason more
        (ux-craft I2), so it is text rather than the prototype's `title` tooltip.
      */}
      {!!reasons?.length && (
        <p
          className="type-perspective-row__why"
          data-testid="type-perspective-row-why"
          data-datum={parity.reasons}
          data-kind="note"
        >
          {reasons.join(' · ')}
        </p>
      )}

      {isOpen && (
        <div className="type-perspective-row__body" data-testid={`type-perspective-row-body-${perspective.system_name}`}>
          {/*
            The contract's `PerspEditor` (`type-perspectives.jsx:76-178`) — the per-field
            override surface, not a second copy of the closed line's readback. The SpiceDB
            relation and the resolved eligible-assignee descriptors stay below it: both are
            server-derived, so no control on this page will ever set them, and rendering them
            as inert facts beside an editor is what says so.
          */}
          {(() => {
            const editorNode = (
              <PerspectiveEditorMolecule
                base={base ?? perspective}
                edits={edits}
                inheritedFromName={inheritedFromName}
                typeName={typeName ?? ''}
                permissionVocabulary={permissionVocabulary}
                assigneeKinds={assigneeKinds}
                readOnly={!editable}
                parity={editorParity}
                onEdit={onEdit}
                onRevert={onRevert}
                data-testid={`type-perspective-row-editor-${perspective.system_name}`}
              />
            );
            return editorPortalNode ? createPortal(editorNode, editorPortalNode) : editorNode;
          })()}

          {perspective.permission_system_relation && (
            <div className="type-perspective-row__relation" data-testid="type-perspective-row-relation">
              <span className="type-perspective-row__relation-label">
                {t('studio.typeEditor.perspectives.relation')}
              </span>
              <TagAtom tone="neutral" size="small" label={perspective.permission_system_relation} />
            </div>
          )}

          <div className="type-perspective-row__assignees" data-testid="type-perspective-row-assignees">
            {!guidance || guidance.unconstrained ? (
              <span className="type-perspective-row__assignees-note">
                {t('studio.typeEditor.perspectives.unconstrained')}
              </span>
            ) : (
              <>
                {guidance.validAssignees.map((assignee) => (
                  <TagAtom
                    key={`${assignee.property}:${assignee.value}`}
                    tone="neutral"
                    size="small"
                    label={assignee.value}
                  />
                ))}
                {guidance.hasUndescribedConstraints && (
                  <span className="type-perspective-row__assignees-note">
                    {t('studio.typeEditor.perspectives.undescribedConstraints')}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

TypePerspectiveRowMolecule.displayName = 'TypePerspectiveRowMolecule';

export default TypePerspectiveRowMolecule;
